<script>
import Breadcrumb from '@/components/global/breadcrumb.vue';
import InputText from 'primevue/inputtext';
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import axios from "axios";
import Swal from "sweetalert2";
import Dropdown from 'primevue/dropdown';
import InputSwitch from 'primevue/inputswitch';
import { useGradeStore } from '@/stores/useGradeStore.js';

export default {
  name: "content-edit",
  inject: {
    courseCtx: { default: null },
  },
  data() {
    return {
      v$: useVuelidate(),
      gradeSlug: this.$route.params.gradeSlug,
      courseId: this.$route.params.courseId ?? null,
      contentId: this.$route.params.id,
      title: "",
      description: "",
      can_skip: false,
      is_file: true,
      fileData: "",
      link: "",
      pdfData: "",           // newly picked PDF to attach/replace (video only)
      existingPdf: null,     // URL of the currently-attached PDF, if any
      removePdf: false,      // admin chose to remove the existing PDF
      posterData: "",        // newly picked poster to attach/replace (video only)
      existingPoster: null,  // URL of the currently-attached poster, if any
      removePoster: false,   // admin chose to remove the existing poster
      formSending: false,
      lesson: null,
      lessons: [],
      unit: null,
      units: [],
      branch: null,
      branches: [],
      type: null,
      typeOptions: [
        { label: 'ملف', value: 'pdf' },
        { label: 'فيديو', value: 'video' },
        { label: 'صوت', value: 'audio' },
        // { label: 'إمتحان', value: 'exam' }
      ],
    };
  },
  components: { Breadcrumb, InputText, Dropdown, InputSwitch },
  validations() {
    return {
      title: { required },
      type: { required },
      is_file: { required },
    };
  },
  computed: {
    isCourseScope() { return !!this.courseId; },
    basePath() {
      return this.isCourseScope ? `/courses/${this.courseId}/contents` : `/${this.gradeSlug}/content`;
    },
    breadcrumbs() {
      if (this.isCourseScope) {
        const base = this.courseCtx?.breadcrumbBase ?? [];
        return [...base, { name: "المحتوى", path: this.basePath }, { name: "تعديل المحتوى", path: `${this.basePath}/edit/${this.contentId}` }];
      }
      return [
        { name: 'الرئيسية', path: '/' },
        { name: "المحتوى", path: `/${this.gradeSlug}/content` },
        { name: "تعديل المحتوى", path: `/${this.gradeSlug}/content/edit/${this.contentId}` },
      ];
    },
    gradeStore() { return useGradeStore(); },
    resolvedGrade() {
      const slug = this.gradeSlug;
      return this.gradeStore.activeGrades.find(g => g.slug === slug)
          || this.gradeStore.allGrades.find(g => g.slug === slug)
          || null;
    },
    gradeId() { return this.resolvedGrade?.id ?? null; },
  },
  methods: {
    onFileChange(event) {
      this.fileData = event.target.files[0];
    },
    onPdfChange(event) {
      this.pdfData = event.target.files[0];
      // Picking a new PDF supersedes a pending removal.
      if (this.pdfData) this.removePdf = false;
    },
    onPosterChange(event) {
      this.posterData = event.target.files[0];
      // Picking a new poster supersedes a pending removal.
      if (this.posterData) this.removePoster = false;
    },

    // ✅ Fetch the existing content data
    fetchContent() {
      axios.get(`/content/${this.contentId}`).then((res) => {
        const data = res.data.data;
        this.title = data.title;
        this.description = data.description;
        this.can_skip = data.can_skip === 1;
        this.is_file = data.is_file === 1;
        this.link = data.link ?? "";
        this.existingPdf = data.pdf_file || null;
        this.existingPoster = data.poster || null;
        this.type = this.typeOptions.find(t => t.value === data.type) || null;

        // fetch lesson hierarchy
        this.fetchBranch().then(() => {
          this.branch = this.branches.find(b => b.id === data.lesson?.parent?.parent?.id) || null;
          if (this.branch) {
            this.fetchUnits().then(() => {
              this.unit = this.units.find(u => u.id === data.lesson?.parent?.id) || null;
              if (this.unit) {
                this.fetchLessons().then(() => {
                  this.lesson = this.lessons.find(l => l.id === data.lesson?.id) || null;
                });
              }
            });
          }
        });
      });
    },

    // ✅ Update instead of create
    sendForm() {
      this.v$.$validate();
      if (!this.v$.$error) {
        const formData = new FormData();
        formData.append('title', this.title);
        formData.append('description', this.description);
        formData.append('lesson_id', this.lesson?.id ?? this.unit?.id ?? this.branch?.id);
        formData.append('type', this.type?.value);
        formData.append('is_file', this.is_file ? 1 : 0);

        if (this.is_file && this.fileData) {
          formData.append('file', this.fileData);
        }
        if (!this.is_file && this.link) {
          formData.append('link', this.link);
        }
        // Optional attached PDF (video content only): replace, remove, or keep.
        if (this.type?.value === 'video') {
          if (this.pdfData) {
            formData.append('pdf_file', this.pdfData);
          } else if (this.removePdf) {
            formData.append('remove_pdf', 1);
          }
        }
        // Optional poster (video content only): replace, remove, or keep.
        if (this.type?.value === 'video') {
          if (this.posterData) {
            formData.append('poster', this.posterData);
          } else if (this.removePoster) {
            formData.append('remove_poster', 1);
          }
        }

        this.formSending = true;
        axios
            .post(`/contents/${this.contentId}?_method=PUT`, formData) // Laravel-friendly way
            .then(() => {
              this.formSending = false;
              Swal.fire({ position: "center", icon: "success", title: "تم التعديل بنجاح" });
            })
            .catch((error) => {
              this.formSending = false;
              Swal.fire({ position: "center", icon: "error", title: error.response.data.message });
            });
      }
    },

    async fetchBranch() {
      const params = this.isCourseScope
          ? { type: 1, course_id: this.courseId }
          : { type: 1, academic_year_id: this.gradeId };
      try {
        const response = await axios.get('/lessons', { params });
        this.branches = response.data.data;
      } catch {
        this.branches = [];
      }
    },
    async fetchUnits() {
      const branchId = this.branch?.id;
      const params = this.isCourseScope
          ? { type: 2, course_id: this.courseId, parent_id: branchId }
          : { type: 2, academic_year_id: this.gradeId, parent_id: branchId };
      try {
        const response = await axios.get('/lessons', { params });
        this.units = response.data.data;
      } catch {
        this.units = [];
      }
    },
    async fetchLessons() {
      const unitId = this.unit?.id;
      const params = this.isCourseScope
          ? { type: 3, course_id: this.courseId, parent_id: unitId }
          : { type: 3, academic_year_id: this.gradeId, parent_id: unitId };
      try {
        const response = await axios.get('/lessons', { params });
        this.lessons = response.data.data;
      } catch {
        this.lessons = [];
      }
    },

    customLabelBranch(option) {
      return option ? (option.description ? `${option.title} (${option.description})` : option.title) : '';
    },
    customLabelUnit(option) {
      return option ? (option.description ? `${option.title} (${option.description})` : option.title) : '';
    },
    customLabelLesson(option) {
      return option ? (option.description ? `${option.title} (${option.description})` : option.title) : '';
    },
  },
  async mounted() {
    if (!this.isCourseScope) {
      if (!this.gradeStore.activeGrades.length) await this.gradeStore.fetchActiveGrades();
      if (!this.gradeStore.allGrades.length) await this.gradeStore.fetchAllGrades();
    }
    this.fetchContent();
  },
};
</script>

<template>
  <Breadcrumb :items="breadcrumbs" />
  <div class="container-fluid my-3">
    <form class="row" @submit.prevent="sendForm()">
      <div class="col-12">
        <div class="tab-radio-group mb-4">
          <label
              v-for="(option, index) in typeOptions"
              :key="index"
              :class="['tab-radio', { active: type?.value === option.value }]"
          >
            <input type="radio" v-model="type" :value="option" class="d-none" />
            {{ option.label }}
          </label>
        </div>
      </div>

      <!-- Branch -->
      <div class="col-lg-4 col-md-6 col-12 mb-4">
        <div class="d-flex flex-column gap-2">
          <label>الفروع</label>
          <Dropdown v-model="branch" :options="branches" showClear :optionLabel="customLabelBranch"
                    placeholder="اختر الفرع" @update:model-value="fetchUnits" />
        </div>
      </div>

      <!-- Unit -->
      <div class="col-lg-4 col-md-6 col-12 mb-4">
        <div class="d-flex flex-column gap-2">
          <label>الوحدات</label>
          <Dropdown v-model="unit" :options="units" showClear :optionLabel="customLabelUnit"
                    placeholder="اختر الوحده" @update:model-value="fetchLessons" />
        </div>
      </div>

      <!-- Lesson -->
      <div class="col-lg-4 col-md-6 col-12 mb-4">
        <div class="d-flex flex-column gap-2">
          <label>الدروس</label>
          <Dropdown v-model="lesson" :options="lessons" showClear :optionLabel="customLabelLesson"
                    placeholder="اختر الدرس" />
        </div>
      </div>

      <!-- Title -->
      <div class="col-lg-4 col-md-6 col-12 mb-4">
        <div class="d-flex flex-column gap-2">
          <label for="title">اسم المحتوى</label>
          <InputText id="title" v-model="title" aria-describedby="title-help" />
          <span class="text-danger" v-if="v$.title.$error">برجاء ادخال اسم المحتوى</span>
        </div>
      </div>

      <!-- Description -->
      <div class="col-lg-4 col-md-6 col-12 mb-4">
        <div class="d-flex flex-column gap-2">
          <label for="description">وصف المحتوى</label>
          <InputText id="description" v-model="description" aria-describedby="description-help" />
        </div>
      </div>
      <div class="col-lg-4 col-md-6 col-12 mb-4"></div>

      <!-- is_file -->
      <div class="col-lg-4 col-md-6 col-12 mb-4">
        <div class="d-flex flex-column gap-2">
          <label for="isFile">هل هو ملف؟</label>
          <InputSwitch id="isFile" v-model="is_file" />
        </div>
      </div>

      <!-- File / Link -->
      <div class="col-lg-4 col-md-6 col-12 mb-4" v-if="is_file">
        <label>الملف</label>
        <input type="file" @change="onFileChange" class="form-control" />
      </div>
      <div class="col-lg-4 col-md-6 col-12 mb-4" v-else>
        <div class="d-flex flex-column gap-2">
          <label for="link">الرابط</label>
          <InputText id="link" v-model="link" aria-describedby="link-help" />
        </div>
      </div>

      <!-- Optional PDF attachment — ONLY for video content (split-screen) -->
      <div class="col-lg-8 col-md-12 col-12 mb-4" v-if="type?.value === 'video'">
        <div class="d-flex flex-column gap-2">
          <label>ملف PDF مرفق (اختياري)</label>
          <div v-if="existingPdf && !removePdf" class="d-flex align-items-center gap-3 mb-1">
            <a :href="existingPdf" target="_blank" class="text-primary">
              <i class="pi pi-file-pdf"></i> عرض ملف PDF الحالي
            </a>
            <button type="button" class="btn btn-sm btn-outline-danger" @click="removePdf = true">
              <i class="pi pi-trash"></i> إزالة
            </button>
          </div>
          <div v-else-if="removePdf" class="text-muted mb-1">
            سيتم إزالة ملف PDF عند الحفظ.
            <button type="button" class="btn btn-sm btn-link p-0" @click="removePdf = false">تراجع</button>
          </div>
          <input type="file" @change="onPdfChange" class="form-control" accept="application/pdf,.pdf" />
          <small class="text-muted">اختر ملفًا لإضافة/استبدال الـ PDF. يظهر أسفل الفيديو للطالب.</small>
        </div>
      </div>

      <!-- Video poster/thumbnail — ONLY for video content -->
      <div class="col-lg-8 col-md-12 col-12 mb-4" v-if="type?.value === 'video'">
        <div class="d-flex flex-column gap-2">
          <label>بوستر الفيديو (اختياري)</label>
          <div v-if="existingPoster && !removePoster" class="d-flex align-items-center gap-3 mb-1">
            <a :href="existingPoster" target="_blank" class="text-primary">
              <i class="pi pi-image"></i> عرض البوستر الحالي
            </a>
            <button type="button" class="btn btn-sm btn-outline-danger" @click="removePoster = true">
              <i class="pi pi-trash"></i> إزالة
            </button>
          </div>
          <div v-else-if="removePoster" class="text-muted mb-1">
            سيتم إزالة البوستر عند الحفظ.
            <button type="button" class="btn btn-sm btn-link p-0" @click="removePoster = false">تراجع</button>
          </div>
          <input type="file" @change="onPosterChange" class="form-control" accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp" />
          <small class="text-muted">يظهر قبل تشغيل الفيديو وأثناء الإيقاف المؤقت بدلاً من صورة عشوائية من الفيديو.</small>
        </div>
      </div>

      <div class="col-lg-4 col-md-6 col-12 mb-4"></div>
      <div class="col-lg-4 col-md-6 col-12">
        <button type="submit" class="btn primaryButton w-100" :disabled="formSending">
          <i class="pi pi-spin pi-spinner-dotted" v-if="formSending"></i>
          {{ formSending ? 'جاري الارسال...' : 'إرسال' }}
        </button>
      </div>
    </form>
  </div>
</template>

<style scoped>
.tab-radio-group {
  display: flex;
  gap: 8px;
}
.tab-radio {
  padding: 8px 20px;
  border: 1px solid #007ad9;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  color: #007ad9;
  width: 25%;
  text-align: center;
}
.tab-radio.active {
  background: #007ad9;
  color: #fff;
  font-weight: bold;
}
</style>
